import type {
  FulfillmentMethod,
  CheckoutCartItem,
  LineItem,
  ManualPaymentMethod,
  PaymentMethodsSnapshot,
  PostalAddress,
} from '@sifly/shared-types'
import { isValidEmail, EMAIL_MAX_LENGTH, normalizeSubdivision } from '@sifly/shared-utils'
import { BUILT_IN_MANUAL_METHODS } from '@sifly/shared-types'

/** US + CA, matching SHIPPING_COUNTRIES on the Stripe path. Anywhere else is
 *  not a jurisdiction our dealers ship to, so collecting it would mislead the
 *  buyer into thinking the order can be fulfilled. */
const SHIPPING_COUNTRIES = ['US', 'CA'] as const

export const CHOOSE_STATE = 'Choose a state.'
export const CHOOSE_PROVINCE = 'Choose a province.'

const MANUAL_METHODS: readonly ManualPaymentMethod[] = [...BUILT_IN_MANUAL_METHODS, 'other']

/**
 * Whether the customer may pay by this method. The storefront UI hides
 * methods the dealer disabled, but hiding is not gating — this is the actual
 * gate. The route calls it before creating anything, so a forged POST cannot
 * invent a payment method the dealer never published for this context, and
 * cannot route card/ACH away from Stripe onto the manual (unpaid-until-the-
 * dealer-confirms) path.
 */
export function isManualMethodEnabled(
  snapshot: PaymentMethodsSnapshot,
  method: ManualPaymentMethod,
  otherId?: string | null,
): boolean {
  if (!MANUAL_METHODS.includes(method)) return false
  if (method === 'other') {
    if (!otherId) return false
    return snapshot.other.some(o => o.id === otherId && o.enabled && o.label.trim() !== '')
  }
  return snapshot[method]?.enabled === true
}

/**
 * Whether the method carries anything the customer could actually ACT on.
 *
 * Separate from `isManualMethodEnabled` because the two answer different
 * questions: that one asks "did the dealer publish this here?", this one asks
 * "did they finish filling it in?". A dealer can tick the Storefront checkbox
 * and save before typing the account details — eFoil Florida has exactly that
 * today, `wire` published to the storefront with a completely empty config —
 * and the result is a live, selectable button leading to a pay page with
 * nothing on it. An offered method with no payload is worse than no method.
 *
 * Deliberately NOT a client-side filter. The cart's snapshot is redacted
 * (storefrontPaymentMethods.ts) precisely so the browser never holds account
 * numbers or instructions, which means the browser has nothing left to judge
 * usability by. So this runs server-side on the full snapshot, in the two
 * places that have it: the cart projection (which decides what is OFFERED) and
 * the route's gate (which decides what is ACCEPTED). Both call it, so the two
 * lists agree by construction rather than by parallel maintenance.
 *
 * `bank_name` / `account_name` alone deliberately do NOT qualify a wire: you
 * cannot send money to a bank's name. The numbers — or free-text instructions
 * carrying them — are the payload.
 */
export function isManualMethodUsable(
  snapshot: PaymentMethodsSnapshot,
  method: ManualPaymentMethod,
  otherId?: string | null,
): boolean {
  const filled = (...values: (string | undefined | null)[]) =>
    values.some(v => typeof v === 'string' && v.trim() !== '')

  switch (method) {
    case 'wire': {
      const w = snapshot.wire
      return filled(w.account_number, w.routing_number, w.swift, w.instructions)
    }
    case 'zelle': {
      const z = snapshot.zelle
      return filled(z.recipient, z.qr_image_url, z.instructions)
    }
    case 'venmo': {
      // Zelle's rule: a handle, a QR, or instructions carrying the handle.
      const v = snapshot.venmo
      return !!v && filled(v.handle, v.qr_image_url, v.instructions)
    }
    case 'check': {
      const c = snapshot.check
      return filled(c.payable_to, c.mailing_address, c.instructions)
    }
    case 'other': {
      if (!otherId) return false
      // For a custom method `instructions` is the ENTIRE payload — there are no
      // structured fields behind it. A dealer who adds "Venmo" and saves before
      // typing the handle has published a dead end.
      const o = snapshot.other.find(m => m.id === otherId)
      return !!o && filled(o.instructions)
    }
    default: {
      // Exhaustive over the registry: a new manual method fails to compile here
      // until it has a rule. At runtime (card/ach, an unknown string) it refuses.
      const unhandled: never = method
      void unhandled
      return false
    }
  }
}

/** Free-text ceilings. Every field they bound is persisted verbatim on
 *  `orders` AND copied onto a contact by `trg_ensure_order_contact`
 *  (migration 172), and Postgres `text` carries no length of its own — so
 *  without these an anonymous POST stores a 200 000-character name on a CRM
 *  row a dealer then has to look at. The Stripe path never had this exposure
 *  because Stripe Checkout collects and bounds these fields itself.
 *
 *  The numbers are deliberately generous — comfortably above the longest real
 *  name or address line — because their job is to bound the write, not to
 *  police the buyer. `email` reuses the platform's own RFC 5321 ceiling rather
 *  than inventing a second number. */
const MAX_NAME_LENGTH = 200
const MAX_PHONE_LENGTH = 40
const MAX_ADDRESS_LINE_LENGTH = 200
const MAX_CITY_LENGTH = 100
const MAX_STATE_LENGTH = 100
const MAX_POSTAL_CODE_LENGTH = 20

/** A line quantity reaches money twice — `line_total = unit_price × quantity`
 *  and `computeShipping` multiplies freight by it — so a body carrying `-3`
 *  produces a NEGATIVE order total and `1e9` produces a nonsense one, both
 *  landing as a real `orders` row plus a contact and an order event. Stripe
 *  rejects a non-positive-integer quantity before any order exists; on this
 *  path nothing does unless it happens here.
 *
 *  99 is the ceiling because the cart drawer is a consumer basket — a dealer
 *  buying pallets goes through the wholesale/quote path, which has a human in
 *  it — and 50 lines is far more than any real storefront cart, so a body
 *  carrying more is a machine, not a shopper. */
export const MAX_LINE_QUANTITY = 99
export const MAX_CART_LINES = 50

export function validateCartItems(
  input: unknown,
): { ok: true; value: CheckoutCartItem[] } | { ok: false; error: string } {
  if (!Array.isArray(input) || input.length === 0) {
    return { ok: false, error: 'Your cart is empty.' }
  }
  if (input.length > MAX_CART_LINES) {
    return { ok: false, error: 'That cart has too many lines.' }
  }
  for (const raw of input) {
    if (typeof raw !== 'object' || raw === null) {
      return { ok: false, error: 'That cart could not be read.' }
    }
    const quantity = (raw as { quantity?: unknown }).quantity
    if (
      typeof quantity !== 'number' ||
      !Number.isInteger(quantity) ||
      quantity < 1 ||
      quantity > MAX_LINE_QUANTITY
    ) {
      return { ok: false, error: `Each item needs a whole quantity between 1 and ${MAX_LINE_QUANTITY}.` }
    }
  }
  return { ok: true, value: input as CheckoutCartItem[] }
}

export type ManualCustomerDetails = {
  email: string
  name: string
  phone?: string | null
  /** null for a store-pickup order — nothing ships, so there is no destination. */
  shipping_address: PostalAddress | null
}

type ValidationResult =
  | { ok: true; value: ManualCustomerDetails }
  | { ok: false; error: string }

/**
 * Stripe Checkout collects email / name / phone / address on the card path.
 * The manual path has no Stripe-hosted page, so nothing else gathers this —
 * and an order the dealer can neither ship nor reply to is worse than no
 * order at all.
 */
export const PHONE_REQUIRED = 'A phone number is required so the store can reach you about your order.'

export function validateCustomerDetails(input: unknown, fulfillmentMethod: FulfillmentMethod = 'ship'): ValidationResult {
  if (typeof input !== 'object' || input === null) {
    return { ok: false, error: 'Missing customer details' }
  }
  const raw = input as Record<string, unknown>
  const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '')

  // Trim + lowercase. The route derives the order's idempotency key from this
  // exact value, so "normalised" has to mean one thing: `Buyer@X.com ` and
  // `buyer@x.com` must reach the same key, or a retry that differs only in
  // casing mints a second order.
  const email = typeof raw.email === 'string' ? raw.email.trim().toLowerCase() : ''
  if (!email || !isValidEmail(email)) {
    return { ok: false, error: 'A valid email is required so we can confirm your order.' }
  }

  const name = typeof raw.name === 'string' ? raw.name.trim() : ''
  if (!name) return { ok: false, error: 'A name is required.' }

  // Every order carries a phone: the dealer must be able to reach the
  // customer about a shipment or a pickup (the hosted page requires one too).
  const phoneRaw = typeof raw.phone === 'string' ? raw.phone.trim() : ''
  if (!phoneRaw) return { ok: false, error: PHONE_REQUIRED }

  // Store pickup: no destination to collect.
  if (fulfillmentMethod === 'pickup') {
    const overLongPickup = (
      [
        ['name', name, MAX_NAME_LENGTH],
        ['email address', email, EMAIL_MAX_LENGTH],
        ['phone number', phoneRaw, MAX_PHONE_LENGTH],
      ] as [string, string, number][]
    ).find(([, value, max]) => value.length > max)
    if (overLongPickup) return { ok: false, error: `That ${overLongPickup[0]} is too long.` }
    return { ok: true, value: { email, name, phone: phoneRaw, shipping_address: null } }
  }

  const addr = raw.shipping_address
  if (typeof addr !== 'object' || addr === null) {
    return { ok: false, error: 'A shipping address is required.' }
  }
  const a = addr as Record<string, unknown>

  const line1 = str(a.line1)
  const city = str(a.city)
  const state = str(a.state)
  const postal_code = str(a.postal_code)
  const country = str(a.country).toUpperCase()

  if (!line1 || !city || !state || !postal_code) {
    return { ok: false, error: 'A complete shipping address is required.' }
  }
  if (!(SHIPPING_COUNTRIES as readonly string[]).includes(country)) {
    return { ok: false, error: 'We can only ship within the US and Canada.' }
  }
  // A real state/province, stored as its code ("wea" reached a live order).
  const stateCode = normalizeSubdivision(country, state)
  if (!stateCode) {
    return { ok: false, error: country === 'CA' ? CHOOSE_PROVINCE : CHOOSE_STATE }
  }

  // Length is checked AFTER shape, so an oversized field never reaches the
  // insert — see the MAX_* block for why an unbounded one is a real write.
  const overLong = (
    [
      ['name', name, MAX_NAME_LENGTH],
      ['email address', email, EMAIL_MAX_LENGTH],
      ['phone number', phoneRaw, MAX_PHONE_LENGTH],
      ['address', line1, MAX_ADDRESS_LINE_LENGTH],
      ['address', str(a.line2), MAX_ADDRESS_LINE_LENGTH],
      ['city', city, MAX_CITY_LENGTH],
      ['state', state, MAX_STATE_LENGTH],
      ['postal code', postal_code, MAX_POSTAL_CODE_LENGTH],
    ] as [string, string, number][]
  ).find(([, value, max]) => value.length > max)
  if (overLong) return { ok: false, error: `That ${overLong[0]} is too long.` }

  return {
    ok: true,
    value: {
      email,
      name,
      phone: phoneRaw,
      shipping_address: { line1, line2: str(a.line2) || undefined, city, state: stateCode, postal_code, country },
    },
  }
}

/** Same pattern as the module-private `roundCents` in
 *  packages/shared-utils/src/shipping.ts (and again in wholesaleFees.ts):
 *  neither is exported from the package index, so a third local copy here
 *  is consistent with existing practice and smaller than widening the
 *  shared package's public API for one caller. */
const roundCents = (n: number): number => Math.round(n * 100) / 100

/** Order total from resolved lines plus shipping. Kept here so the route
 *  never arithmetics money inline — a later change to how shipping or line
 *  totals combine has one place to change.
 *
 *  `orders.total` is money a dealer reconciles against a bank statement, so
 *  a total that drifts off the cent (10.10 + 20.20 = 30.299999999999997 in
 *  IEEE-754) is a real defect, not a cosmetic one — round at this return
 *  boundary the same way computeShipping and wholesaleFees round at theirs.
 *  `shippingCharged` is already rounded by computeShipping's own boundary,
 *  so it needs no re-rounding here, but subtotal + shipping can still
 *  reintroduce sub-cent drift, so `total` gets its own rounding too. */
export function manualOrderTotals(snapshots: LineItem[], shippingCharged: number) {
  const subtotal = roundCents(snapshots.reduce((sum, li) => sum + li.line_total, 0))
  return { subtotal, shipping: shippingCharged, total: roundCents(subtotal + shippingCharged) }
}

